// Saved Games Modal Component
// Displays user's saved/favorited games

import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { HeartGame } from '../../../types/heartGames';

// Modal styled components
const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #1a1a1a; /* Dark background for modal */
  border-radius: 20px;
  padding: 1.5rem;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s ease;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SavedGameItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const GameIconContainer = styled.div`
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GameInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GameName = styled.h3`
  margin: 0;
  color: white;
  font-size: 1rem;
  font-weight: 600;
`;

const GameDescription = styled.p`
  margin: 0.25rem 0 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyStateText = styled.p`
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

interface SavedGamesModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedGames: HeartGame[];
  onGameClick: (gameId: string) => void;
}

export const SavedGamesModal: React.FC<SavedGamesModalProps> = ({
  isOpen,
  onClose,
  savedGames,
  onGameClick,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Saved Games</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {savedGames.length === 0 ? (
          <EmptyState>
            <p>No saved games yet</p>
            <EmptyStateText>
              Tap the bookmark icon on any game to save it
            </EmptyStateText>
          </EmptyState>
        ) : (
          <div>
            {savedGames.map((game) => (
              <SavedGameItem
                key={game.id}
                onClick={() => {
                  onGameClick(game.id);
                  onClose();
                }}
              >
                <GameIconContainer>{game.icon}</GameIconContainer>
                <GameInfo>
                  <GameName>{game.name}</GameName>
                  <GameDescription>{game.description}</GameDescription>
                </GameInfo>
              </SavedGameItem>
            ))}
          </div>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

